import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Empty, Pagination } from '@bfe/moly';
import { getSearchParams } from '@web3/base-utils';
import { useFm, useHybirdWallet } from '@web3/base-hooks';
import { initialWindowType } from '@/hooks/media';
import { RankType, REFRESH_REMIND_TIME } from '@/constants';
import { getProjectList } from '@/services/home';
import { ActiveStatus } from '@/hooks/home';
import Top3 from './Top3';
import TopMore from './TopMore';

import styles from './projectList.module.scss';

type Props = {
  rankType?: RankType;
  activeStatus: ActiveStatus;
  topRef?: React.MutableRefObject<any>;
};

const MOBILE_PAGE_SIZE = 10;
const PC_PAGE_SIZE = 20;
const PC_FIRST_PAGE_SIZE = 15;

const ProjectList = ({ rankType, activeStatus, topRef }: Props) => {
  const { isMobile } = initialWindowType;
  const fm = useFm();
  const [page, setPage] = useState(1);
  const PAGE_SIZE = useMemo(() => {
    if (isMobile) {
      return MOBILE_PAGE_SIZE;
    } else {
      return page === 1 ? PC_FIRST_PAGE_SIZE : PC_PAGE_SIZE;
    }
  }, [page, isMobile]);

  const [projectList, setProjectList] = useState([]);
  const [remainVoteCount, setRemainVoteCount] = useState(3);
  const { walletId, walletType } = useHybirdWallet();

  const scrollToTop = () => {
    topRef?.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const getList = useCallback(() => {
    const searchParams = getSearchParams() as { activityCode?: string };
    getProjectList({
      activityCode: searchParams?.activityCode,
      walletId,
      walletType,
    }).then((res) => {
      if (res?.success) {
        let resList = res?.result?.projects;
        // 排名赋值
        resList = resList?.map((item, index) => ({
          ...item,
          rank: index + 1,
        }));
        setRemainVoteCount(res?.result?.remainLotteryCount);
        setProjectList(resList);
        setPage(1);
      }
    });
  }, [walletId, walletType]);

  useEffect(() => {
    getList();
  }, [walletId, walletType]);

  // 定时刷新
  useEffect(() => {
    const timer = setInterval(
      () => {
        getList();
      },
      REFRESH_REMIND_TIME * 60 * 1000
    );

    return () => {
      clearInterval(timer);
    };
  }, []);

  const filterProjectList = useMemo(() => {
    setPage(1);
    // 无筛选
    return projectList;
  }, [projectList]);

  const isResolvedTop3 = useMemo(() => {
    if (
      filterProjectList.length >= 3 &&
      filterProjectList[0].point > 0 &&
      filterProjectList[1].point > 0 &&
      filterProjectList[2].point > 0
    ) {
      return true;
    }
    return false;
  }, [filterProjectList]);

  // 分页
  const currentPageList = useMemo(() => {
    let start;
    if (isMobile) {
      start = (page - 1) * PAGE_SIZE;
    } else {
      start = (page - 2) * PAGE_SIZE + PC_FIRST_PAGE_SIZE;
    }
    const total = filterProjectList?.length;
    const end = start + PAGE_SIZE < total ? start + PAGE_SIZE : total;
    return filterProjectList?.slice(start, end) || [];
  }, [filterProjectList, page, PAGE_SIZE, isMobile]);

  const top3List = useMemo(
    () => currentPageList?.slice(0, 3) || [],
    [currentPageList]
  );
  const othersList = useMemo(
    () => currentPageList?.slice(3, PC_FIRST_PAGE_SIZE) || [],
    [currentPageList]
  );

  const UnResolvedTop3 = () => {
    return (
      <TopMore
        list={filterProjectList}
        activeStatus={activeStatus}
        remainVoteCount={remainVoteCount}
        allNormal={true}
      />
    );
  };

  const ResolvedTop3 = () => {
    return (
      <>
        {filterProjectList?.length === 0 && <Empty title={fm('noDataFound')} />}
        {rankType === RankType.POINTS && page === 1 ? (
          <>
            <Top3
              list={top3List}
              activeStatus={activeStatus}
              remainVoteCount={remainVoteCount}
            />
            <TopMore
              allNormal={false}
              list={othersList}
              activeStatus={activeStatus}
              remainVoteCount={remainVoteCount}
            />
          </>
        ) : (
          <TopMore
            allNormal={false}
            list={currentPageList}
            activeStatus={activeStatus}
            remainVoteCount={remainVoteCount}
          />
        )}

        <Pagination
          current={page}
          pageSize={PAGE_SIZE}
          total={
            isMobile ? filterProjectList?.length : filterProjectList?.length
          }
          onChange={(current) => {
            setPage(current);
            scrollToTop();
          }}
          showTotal={() => {
            if (isMobile) return null;
            const total = filterProjectList?.length;
            const start = (page - 2) * PAGE_SIZE + PC_FIRST_PAGE_SIZE + 1;
            const end =
              start + PAGE_SIZE < total ? start + PAGE_SIZE - 1 : total;
            return `${start}-${end} of ${total} items`;
          }}
          showQuickJumper={!isMobile}
          hideOnSinglePage
        />
      </>
    );
  };

  return (
    <div className={styles.wrapper}>
      {isResolvedTop3 ? <ResolvedTop3 /> : <UnResolvedTop3 />}
    </div>
  );
};

export default ProjectList;
